import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  validateLicense,
  getCategories,
  saveAccessPoint,
  editAccessPoint,
} from "../../services/apServices";
import {
  Check as CheckIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  PublicOutlined as PublicOutlinedIcon,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
} from "@material-ui/icons";
import { DropzoneArea } from "material-ui-dropzone";
import LocationPicker from "../LocationPicker";
import Swal from "sweetalert2";
import "./sweetalertstyle.css";
import LocationInputs from "./LocationInputs";

const WizardShell = styled.div`
  background: linear-gradient(180deg, #f4f8fc 0%, #eef4fb 100%);
  border-radius: 28px;
  overflow: hidden;
`;

const HeroPanel = styled.div`
  padding: 24px 28px;
  background: linear-gradient(135deg, #0f2948 0%, #1d4e89 52%, #67b5de 100%);
  color: #fff;
`;

const HeroKicker = styled(Typography)`
  opacity: 0.72;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-size: 0.72rem;
  font-weight: 700;
`;

const HeroTitle = styled(Typography)`
  margin-top: 8px;
  font-weight: 800;
`;

const HeroDescription = styled(Typography)`
  margin-top: 8px;
  max-width: 720px;
  opacity: 0.92;
`;

const HeroStat = styled.div`
  min-width: 170px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
`;

const StepperPanel = styled.div`
  padding: 20px 24px 0;
  background: #fff;
`;

const ContentPanel = styled.div`
  padding: 24px;
  background: #fff;
`;

const IntroCard = styled.div`
  height: 100%;
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.06);
`;

const SectionCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid rgba(226, 232, 240, 0.94);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-weight: 800;
  color: #10213a;
`;

const SectionDescription = styled(Typography)`
  color: #64748b;
  margin-top: 6px;
`;

const MetricBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(29, 78, 137, 0.08);
  color: #1d4e89;
  font-weight: 700;
`;

const MetricBadgeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(29, 78, 137, 0.08);
  color: #1d4e89;
  font-weight: 700;
  border: 1px solid rgba(29, 78, 137, 0.12);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(29, 78, 137, 0.14);
    transform: translateY(-1px);
  }
`;

const InputCard = styled.div`
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid rgba(226, 232, 240, 0.92);
`;

const UploadShell = styled.div`
  margin-top: 16px;

  .MuiDropzoneArea-root {
    min-height: 170px;
    border-radius: 22px;
    border: 2px dashed rgba(148, 163, 184, 0.34);
    background: linear-gradient(180deg, #fbfdff 0%, #f6faff 100%);
  }

  .MuiDropzoneArea-icon {
    color: #1d4e89;
  }

  .MuiDropzoneArea-text {
    color: #475569;
    font-weight: 600;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 28px;

  @media (max-width: 600px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const LeftActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const RightActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const SecondaryButton = styled(Button)`
  && {
    border-radius: 14px;
    padding: 10px 18px;
    text-transform: none;
    font-weight: 700;
  }
`;

const PrimaryButton = styled(Button)`
  && {
    border-radius: 14px;
    padding: 10px 20px;
    text-transform: none;
    font-weight: 700;
    box-shadow: 0 12px 24px rgba(29, 78, 137, 0.18);
  }
`;

const StatusCard = styled.button`
  width: 100%;
  padding: 22px;
  border-radius: 24px;
  border: 1px solid
    ${(props) =>
      props.$active ? "rgba(29, 78, 137, 0.48)" : "rgba(226, 232, 240, 0.95)"};
  background: ${(props) =>
    props.$active
      ? "linear-gradient(180deg, rgba(29, 78, 137, 0.08) 0%, rgba(103, 181, 222, 0.08) 100%)"
      : "#fff"};
  box-shadow: ${(props) =>
    props.$active
      ? "0 16px 34px rgba(29, 78, 137, 0.12)"
      : "0 16px 34px rgba(15, 23, 42, 0.05)"};
  text-align: left;
  cursor: pointer;
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
`;

const Notice = styled(Typography)`
  margin-top: 10px;
  color: #dc2626;
  font-weight: 600;
`;

const stepMeta = [
  {
    kicker: "Paso 1 de 3",
    title: "Valida tu licencia antes de activar el punto",
    description:
      "Confirma que la licencia está disponible y lista para enlazarse con el access point que vas a registrar.",
  },
  {
    kicker: "Paso 2 de 3",
    title: "Configura los detalles visibles del access point",
    description:
      "Define nombre, categoría, ubicación exacta e imagen de vista previa con una estructura más clara y operativa.",
  },
  {
    kicker: "Paso 3 de 3",
    title: "Elige el modo de publicación del AP",
    description:
      "Decide si el punto trabajará con contenido público o si será exclusivo para el propietario.",
  },
];

function getSteps() {
  return ["Registrar License Key", "AP Detalles", "Seleccionar Estado"];
}

const licenseValidationSchema = Yup.object({
  licenseKey: Yup.string()
    .length(16, "License key must be 16 characters")
    .required("License key is required"),
});

const apDetailsValidationSchema = Yup.object({
  name: Yup.string().required("¡Requerido!"),
  categoryId: Yup.string().required("¡Requerido!"),
  previewImage: Yup.mixed().required("¡Requerido!"),
});

export default function ApRegistrationWizard({ editing, dataAP }) {
  const [activeStep, setActiveStep] = useState(!editing ? 0 : 1);
  const [licenseValid, setLicenseValid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locationRequestSignal, setLocationRequestSignal] = useState(0);
  const [formData, setFormData] = useState({
    licenseKey: dataAP?.licenseKey || "",
    name: dataAP?.name || "",
    location: {
      latitude: dataAP?.latitude || "",
      longitude: dataAP?.longitude || "",
    },
    previewImage: dataAP?.previewImage || "",
    categoryId: dataAP?.categoryId || "",
    status: dataAP?.status || "",
  });
  const [categories, setCategories] = useState([]);

  const steps = getSteps();
  const currentStepMeta = stepMeta[activeStep];

  useEffect(() => {
    getCategories().then((res) => {
      setCategories(res.data);
    });
  }, []);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleLicenseSubmit = async (license) => {
    setLoading(true);
    setLicenseValid(null);

    try {
      const response = await validateLicense(license);
      const exists = response.data.exists;
      setLicenseValid(exists);

      if (exists) {
        setFormData((prev) => ({
          ...prev,
          licenseKey: license,
        }));
      }
    } catch (error) {
      console.error("License validation failed:", error);
      setLicenseValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = (statusValue) => {
    setFormData((prev) => ({
      ...prev,
      status: statusValue,
    }));
  };

  const handleSubmitFinal = async () => {
    try {
      const form = new FormData();
      const clientData = JSON.stringify({
        name: formData.name,
        latitude: formData.location.latitude,
        longitude: formData.location.longitude,
        categoryId: formData.categoryId,
        status: formData.status,
      });

      form.append("data", clientData);
      form.append("image", formData.previewImage);

      if (editing) {
        await editAccessPoint(form, dataAP.id);
      } else {
        await saveAccessPoint(form, formData.licenseKey);
      }

      Swal.fire("Guardado!", "AP guardado exitosamente.", "success");

      setActiveStep(0);
      setFormData({
        licenseKey: "",
        name: "",
        categoryId: "",
        previewImage: "",
        location: {
          latitude: "",
          longitude: "",
        },
        status: "",
      });
      setLicenseValid(null);
      window.location.reload();
    } catch (error) {
      console.error("Submit error:", error);
      Swal.fire("Error!", "Error guardando el AP.", "error");
    }
  };

  return (
    <WizardShell>
      <HeroPanel>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={8}>
            <HeroKicker variant="body2">{currentStepMeta.kicker}</HeroKicker>
            <HeroTitle variant="h4">{currentStepMeta.title}</HeroTitle>
            <HeroDescription variant="body1">
              {currentStepMeta.description}
            </HeroDescription>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <HeroStat>
                  <Typography variant="caption">Modo</Typography>
                  <Typography variant="h6">
                    {editing ? "Edicion" : "Registro"}
                  </Typography>
                </HeroStat>
              </Grid>
              <Grid item>
                <HeroStat>
                  <Typography variant="caption">Progreso</Typography>
                  <Typography variant="h6">{activeStep + 1}/3</Typography>
                </HeroStat>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HeroPanel>

      <StepperPanel>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </StepperPanel>

      <ContentPanel>
        {activeStep === 0 && (
          <Formik
            initialValues={{ licenseKey: formData.licenseKey }}
            validationSchema={licenseValidationSchema}
          >
            {({ values, handleChange, handleBlur, touched, errors }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <IntroCard>
                      <Avatar style={{ background: "#1d4e89" }}>
                        <VpnKeyOutlinedIcon />
                      </Avatar>
                      <SectionTitle variant="h6" style={{ marginTop: 18 }}>
                        Enlace seguro de licencia
                      </SectionTitle>
                      <SectionDescription variant="body2">
                        Usa una licencia valida de 16 caracteres para garantizar
                        que el AP quede asociado al inventario correcto.
                      </SectionDescription>
                      <MetricBadge>
                        <CheckIcon fontSize="small" /> Validacion previa al alta
                      </MetricBadge>
                    </IntroCard>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <SectionCard>
                      <SectionTitle variant="h6">
                        Introduce la license key
                      </SectionTitle>
                      <SectionDescription variant="body2">
                        Verifica la clave antes de pasar a la configuracion
                        visual y geografica del punto.
                      </SectionDescription>

                      <Box mt={3}>
                        <TextField
                          name="licenseKey"
                          label="License Key"
                          variant="outlined"
                          fullWidth
                          onChange={(event) => {
                            setLicenseValid(null);
                            handleChange({
                              target: {
                                name: "licenseKey",
                                value: event.target.value,
                              },
                            });
                          }}
                          onBlur={handleBlur}
                          value={values.licenseKey}
                          error={
                            touched.licenseKey && Boolean(errors.licenseKey)
                          }
                          helperText={touched.licenseKey && errors.licenseKey}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <SecondaryButton
                                  disabled={
                                    values.licenseKey.length !== 16 || loading
                                  }
                                  onClick={async () => {
                                    if (values.licenseKey) {
                                      await handleLicenseSubmit(
                                        values.licenseKey
                                      );
                                    }
                                  }}
                                  color="primary"
                                  variant="outlined"
                                >
                                  {loading ? (
                                    <CircularProgress size={20} />
                                  ) : licenseValid ? (
                                    "Valida"
                                  ) : (
                                    "Comprobar"
                                  )}
                                </SecondaryButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {licenseValid === false && !loading && (
                        <Notice variant="body2">License key is invalid.</Notice>
                      )}

                      {licenseValid && (
                        <MetricBadge>
                          <CheckIcon fontSize="small" /> Licencia validada
                        </MetricBadge>
                      )}

                      <ActionBar>
                        <LeftActions />
                        <RightActions>
                          <PrimaryButton
                            disabled={
                              loading ||
                              !values.licenseKey ||
                              Boolean(errors.licenseKey) ||
                              !licenseValid
                            }
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            fullWidth
                          >
                            Continuar
                          </PrimaryButton>
                        </RightActions>
                      </ActionBar>
                    </SectionCard>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        )}

        {activeStep === 1 && (
          <Formik
            initialValues={{
              name: formData.name,
              categoryId: formData.categoryId,
              previewImage: formData.previewImage,
              location: formData.location,
            }}
            validationSchema={apDetailsValidationSchema}
            onSubmit={(values) => {
              setFormData((prev) => ({
                ...prev,
                name: values.name,
                categoryId: values.categoryId,
                previewImage: values.previewImage,
                location: values.location,
              }));
              handleNext();
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              setFieldValue,
              touched,
              errors,
            }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <InputCard>
                      <SectionTitle variant="h6">Nombre del AP</SectionTitle>
                      <SectionDescription variant="body2">
                        Usa un nombre claro y reconocible para operacion y
                        soporte.
                      </SectionDescription>
                      <Box mt={3}>
                        <TextField
                          name="name"
                          label="AP Name"
                          variant="outlined"
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.name}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Box>
                    </InputCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputCard>
                      <SectionTitle variant="h6">
                        Categoria operativa
                      </SectionTitle>
                      <SectionDescription variant="body2">
                        Clasifica el AP para facilitar segmentacion, filtros y
                        reportes.
                      </SectionDescription>
                      <Box mt={3}>
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="select-category">Category</InputLabel>
                          <Select
                            labelId="select-category"
                            name="categoryId"
                            label="Category"
                            variant="outlined"
                            value={values.categoryId}
                            onChange={handleChange}
                            error={
                              touched.categoryId && Boolean(errors.categoryId)
                            }
                          >
                            {categories.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {touched.categoryId && errors.categoryId && (
                          <Notice variant="body2">{errors.categoryId}</Notice>
                        )}
                      </Box>
                    </InputCard>
                  </Grid>

                  <Grid item xs={12}>
                    <SectionCard>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={7}>
                          <SectionTitle variant="h6">
                            Ubicacion precisa del access point
                          </SectionTitle>
                          <SectionDescription variant="body2">
                            Busca por coordenadas o selecciona el punto
                            directamente en el mapa para guardar una referencia
                            exacta.
                          </SectionDescription>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <MetricBadgeButton
                            type="button"
                            onClick={() =>
                              setLocationRequestSignal((current) => current + 1)
                            }
                          >
                            <PlaceOutlinedIcon fontSize="small" /> Utilizar mi
                            ubicacion actual
                          </MetricBadgeButton>
                        </Grid>
                      </Grid>

                      <Box mt={3}>
                        <LocationPicker
                          location={values.location}
                          requestLocationSignal={locationRequestSignal}
                          onLocationSelect={(loc) =>
                            setFieldValue("location", loc)
                          }
                        />
                      </Box>

                      <LocationInputs
                        values={values}
                        setFieldValue={setFieldValue}
                      />

                      {(values.location?.longitude === "" ||
                        values.location?.latitude === "") && (
                        <Notice variant="body2">¡Requerido!</Notice>
                      )}
                    </SectionCard>
                  </Grid>

                  <Grid item xs={12}>
                    <SectionCard>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={7}>
                          <SectionTitle variant="h6">
                            Vista previa del access point
                          </SectionTitle>
                          <SectionDescription variant="body2">
                            Sube una imagen ligera y nitida para identificar el
                            AP en catalogos y listados internos.
                          </SectionDescription>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <MetricBadge>
                            <CloudUploadOutlinedIcon fontSize="small" /> JPG o
                            PNG, maximo 1 MB
                          </MetricBadge>
                        </Grid>
                      </Grid>

                      <UploadShell>
                        <DropzoneArea
                          inputProps={{
                            name: "previewImage",
                            id: "previewImage",
                          }}
                          filesLimit={1}
                          showPreviewsInDropzone
                          showFileNamesInPreview
                          showFileNames
                          clearOnUnmount
                          maxFileSize={1048576}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          onChange={(loadedFiles) => {
                            setFieldValue("previewImage", loadedFiles[0]);
                          }}
                        />
                      </UploadShell>

                      {typeof values.previewImage === "string" &&
                        values.previewImage && (
                          <MetricBadge>
                            <CheckIcon fontSize="small" /> Imagen actual cargada
                          </MetricBadge>
                        )}

                      {!values.previewImage && (
                        <Notice variant="body2">¡Requerido!</Notice>
                      )}
                    </SectionCard>
                  </Grid>
                </Grid>

                <ActionBar>
                  <LeftActions>
                    {activeStep > 0 && (
                      <SecondaryButton
                        disabled={editing}
                        onClick={handleBack}
                        variant="outlined"
                        color="primary"
                        fullWidth
                      >
                        Atras
                      </SecondaryButton>
                    )}
                  </LeftActions>
                  <RightActions>
                    <PrimaryButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      Continuar
                    </PrimaryButton>
                  </RightActions>
                </ActionBar>
              </Form>
            )}
          </Formik>
        )}

        {activeStep === 2 && (
          <SectionCard>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StatusCard
                  type="button"
                  $active={formData.status === "PUBLICO"}
                  onClick={() => handleChangeStatus("PUBLICO")}
                >
                  <StatusHeader>
                    <Box display="flex" alignItems="center" gridGap={12}>
                      <Avatar style={{ background: "#1d4e89" }}>
                        <PublicOutlinedIcon />
                      </Avatar>
                      <div>
                        <SectionTitle variant="h6">Status Publico</SectionTitle>
                        <SectionDescription variant="body2">
                          Monetiza contenido visible para la audiencia general
                          del AP.
                        </SectionDescription>
                      </div>
                    </Box>
                    <Radio
                      color="primary"
                      checked={formData.status === "PUBLICO"}
                      value="PUBLICO"
                    />
                  </StatusHeader>
                  <Typography variant="body2" color="textSecondary">
                    En este modo el AP monetizara el contenido que sea publicado
                    en este, excepto las publicaciones que sean propias del
                    dueno del AP.
                  </Typography>
                </StatusCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StatusCard
                  type="button"
                  $active={formData.status === "PRIVADO"}
                  onClick={() => handleChangeStatus("PRIVADO")}
                >
                  <StatusHeader>
                    <Box display="flex" alignItems="center" gridGap={12}>
                      <Avatar style={{ background: "#334155" }}>
                        <LockOutlinedIcon />
                      </Avatar>
                      <div>
                        <SectionTitle variant="h6">Status Privado</SectionTitle>
                        <SectionDescription variant="body2">
                          Restringe el acceso visual y operativo al dueno del
                          AP.
                        </SectionDescription>
                      </div>
                    </Box>
                    <Radio
                      color="primary"
                      checked={formData.status === "PRIVADO"}
                      value="PRIVADO"
                    />
                  </StatusHeader>
                  <Typography variant="body2" color="textSecondary">
                    En este modo el AP solo podra ser visualizado por el dueno
                    del AP y solo contenido del dueno de este AP puede ser
                    publicado.
                  </Typography>
                </StatusCard>
              </Grid>
            </Grid>

            {!formData.status && (
              <Notice variant="body2">
                Selecciona un estado para continuar.
              </Notice>
            )}

            <ActionBar>
              <LeftActions>
                <SecondaryButton
                  onClick={handleBack}
                  variant="outlined"
                  color="primary"
                  fullWidth
                >
                  Atras
                </SecondaryButton>
              </LeftActions>
              <RightActions>
                <PrimaryButton
                  onClick={handleSubmitFinal}
                  variant="contained"
                  color="primary"
                  disabled={!formData.status}
                  fullWidth
                >
                  Guardar AP
                </PrimaryButton>
              </RightActions>
            </ActionBar>
          </SectionCard>
        )}
      </ContentPanel>
    </WizardShell>
  );
}
